import { GalleryLink } from '@tribal-worldwide-london/ui-component-library-vwg';
import {
  DesignTokenSizeEnum,
  Text,
  TokenTextAppearance,
  TextTag,
  useTheme,
} from '@volkswagen-onehub/components-core';
import { useState } from 'react';
import {
  performVWBasicButtonClick,
  performVWBasicLayerClose,
  performVWBasicLayerLoad,
  performVWBasicMultipleVehiclesButtonClick,
} from '@/analytics/events';
import { SectionNames } from '@/analytics/sectionNames';
import { URL_GREYED_OUT_CAR } from '@/constants/images';
import { useTribalContext } from '@/context/tribal';
import useAppNavigate, { PATHS } from '@/hooks/useAppNavigate';
import useResizeResponsive from '@/hooks/useResizeResponsive';
import useGlobalStore, {
  GlobalStoreState,
  GlobalStoreStateConfiguration,
} from '@/stores/globalStore';
import {
  Box,
  CTAMail,
  FullscreenLayer,
  PageContainer,
  VWImageQuality,
  VehicleToggleButtonCarousel,
} from '@/modules/components';
import {
  BackgroundVals,
  VehicleToggleButtonProps,
} from '@/modules/components/VehicleToggleButton/VehicleToggleButton';
import GalleryViewer from '@/modules/features/single/GalleryViewer/GalleryViewer';
import { isMobile } from '@/modules/utils/breakpoints';
import {
  getConfigImage,
  getDisabledModelMessage,
  getModelTrim,
  isNotBuildable,
  isStockCar,
} from '@/modules/utils/storeUtils';
import {
  Background,
  CarAndGalleryWrapper,
  CompareCTA,
  HeaderBackgroundTextWrapper,
  HeaderWrapper,
  StyledHeroImage,
  Title,
  TitleAndVehicleCarouselWrapper,
  VehicleButtonCarouselWrapper,
} from './Header.styles';
import HeaderBackgroundText from './HeaderBackgroundText';

interface HeaderProps {
  appData: GlobalStoreState['app'];
  activeConfiguration: GlobalStoreStateConfiguration;
}

function Header({ appData, activeConfiguration }: HeaderProps) {
  const { contentManager, analyticsManager, analyticsBuilder } =
    useTribalContext();
  const navigate = useAppNavigate();
  const isMobileWidth = useResizeResponsive(isMobile);
  const [galleryViewerOpen, setGalleryViewerOpen] = useState(false);
  const contactEmail = appData.retailerContact?.contact.contactEmail;
  const contactRetailerLabel = contentManager.get('ctas-contact-retailer');
  const { retailers, configurations } = useGlobalStore((state) => state.app);
  const theme = useTheme();

  const analyticsCommonFields = {
    sectionName: SectionNames.SINGLE_PAGE_HEADER,
    configuration: activeConfiguration,
    retailers,
    analyticsManager,
    analyticsBuilder,
  };

  const PrimaryCTA = () =>
    contactEmail ? (
      <CTAMail
        ctaProps={{ emphasis: 'primary' }}
        mailto={contactEmail}
        onClick={() => {
          performVWBasicButtonClick({
            label: contactRetailerLabel,
            ...analyticsCommonFields,
          });
        }}
      >
        {contactRetailerLabel}
      </CTAMail>
    ) : null;

  const VehicleToggleButtons = ({
    background,
  }: {
    background: BackgroundVals;
  }) => {
    const vehicleToggleButtonsData = appData.configurations.map(
      (configuration: GlobalStoreStateConfiguration, index: number) => {
        const {
          pdbData,
          orderNumber,
          marketingShortCode,
          modelTitle,
          trimTitle,
          derivativeTitle
        } = configuration;
        const image = getConfigImage(configuration, 'exterior-side').uri;

        let vehicleProps = {
          active: index === appData.activeConfiguration,
          id: marketingShortCode || orderNumber,
          stockBadgeLabel: contentManager.get('stock-badge-label'),
          stockInfo: contentManager.get('stock-badge-info'),
          selectedText: contentManager.get('selected-vehicle-text'),
          background,
        };

        if (isNotBuildable(pdbData)) {
          const disabledMessage = getDisabledModelMessage(
            contentManager,
            modelTitle,
            derivativeTitle || trimTitle
          );

          return {
            ...vehicleProps,
            imageUrl: URL_GREYED_OUT_CAR,
            title: disabledMessage,
            text: '',
            disabled: isNotBuildable(pdbData),
          } as VehicleToggleButtonProps;
        } else {
          return {
            ...vehicleProps,
            imageUrl: image || '',
            title: getModelTrim(
              pdbData?.derivative?.modelTitle,
              pdbData?.derivative?.trimTitle
            ),
            text: configuration.pdbData?.derivative?.title || '',
            onClick: () => navigate(configuration.fullUrl),
            stock: isStockCar(configuration),
            stockBadgeOpenCallback: () =>
              performVWBasicLayerLoad(analyticsCommonFields),
            stockBadgeCloseCallback: () =>
              performVWBasicLayerClose(analyticsCommonFields),
          } as VehicleToggleButtonProps;
        }
      }
    );

    const handleCompareCtaClick = () => {
      performVWBasicMultipleVehiclesButtonClick({
        label: contentManager.get('ctas-compare'),
        ...analyticsCommonFields,
        configuration: null,
        configurations
      });
      navigate(PATHS.compare);
    };

    return (
      <VehicleButtonCarouselWrapper
        vehiclesLength={appData.configurations.length}
      >
        <VehicleToggleButtonCarousel
          background={background}
          vehicleToggleButtons={vehicleToggleButtonsData}
          activeSlide={appData.activeConfiguration}
        />
        {appData.configurations.length > 1 && (
          <CompareCTA
            tag="button"
            emphasis="secondary"
            onClick={handleCompareCtaClick}
            styles={
              isMobileWidth
                ? undefined
                : {
                  backgroundColor: theme.colors.background.primary,
                  borderColor: 'transparent',
                }
            }
          >
            {contentManager.get('ctas-compare')}
          </CompareCTA>
        )}
      </VehicleButtonCarouselWrapper>
    );
  };

  const openGallery = () => {
    setGalleryViewerOpen(true);

    performVWBasicButtonClick({
      label: contentManager.get('ctas-gallery'),
      ...analyticsCommonFields,
    });
  };

  const yourWagenTitleEnabled = contentManager.get('yourwagen-title-enabled') === 'true'


  const PersonalisedHeader = () => {
    if (appData?.message?.parameters?.customerFirstName) {
      return (
        <>
          <Text bold appearance={TokenTextAppearance.headline400}>
            {`${contentManager.get('yourwagen-title-pre')} ${appData?.message?.parameters?.customerFirstName}${contentManager.get('yourwagen-title-post')}`}
          </Text>
  
          <Text appearance={TokenTextAppearance.headline300}>
            {contentManager.get('yourwagen-subtitle')}
          </Text>
        </>
      )
    }
  }

  return (
    <HeaderWrapper>
      <Background>
        <HeaderBackgroundTextWrapper>
          <HeaderBackgroundText>
            {activeConfiguration?.pdbData?.derivative?.modelTitle}
          </HeaderBackgroundText>
        </HeaderBackgroundTextWrapper>
      </Background>

      <PageContainer>
        <TitleAndVehicleCarouselWrapper>
          <div>
            <Title appearance={TokenTextAppearance.headline500} tag={TextTag.h1}>
              {contentManager.get('single-page-welcome-text')}
              {!yourWagenTitleEnabled && (
                <Text bold tag={TextTag.span}>
                  {' ' + appData?.message?.parameters?.customerFirstName}
                </Text>
              )}
            </Title>

            {/* Say Hello to [NAME]Wagen header */}
            {yourWagenTitleEnabled && PersonalisedHeader()}
            
            {yourWagenTitleEnabled && !isMobileWidth && <Box mb={DesignTokenSizeEnum.static300}></Box>}
            {!isMobileWidth && <PrimaryCTA />}
          </div>

          {!isMobileWidth && (
            <VehicleToggleButtons background={BackgroundVals.TRANSPARENT} />
          )}
        </TitleAndVehicleCarouselWrapper>

        <CarAndGalleryWrapper>
          <StyledHeroImage
            src={getConfigImage(activeConfiguration, 'exterior-front').uri}
            quality={VWImageQuality.HIGH}
            alt=""
            objectFit="contain"
          />
          <Box
            display="flex"
            alignItems="center"
            mb={DesignTokenSizeEnum.static250}
          >
            <GalleryLink
              emphasis="secondary"
              label={contentManager.get('ctas-gallery')}
              mainImage={
                getConfigImage(activeConfiguration, 'exterior-rear').uri
              }
              leftImage={
                getConfigImage(activeConfiguration, 'exterior-side').uri
              }
              rightImage={
                getConfigImage(activeConfiguration, 'interior-front').uri
              }
              onClick={openGallery}
            />
          </Box>

          {isMobileWidth && <PrimaryCTA />}
          {isMobileWidth && (
            <VehicleToggleButtons background={BackgroundVals.FILLED} />
          )}
        </CarAndGalleryWrapper>
        {galleryViewerOpen && (
          <FullscreenLayer active={galleryViewerOpen}>
            <GalleryViewer onBack={() => setGalleryViewerOpen(false)} />
          </FullscreenLayer>
        )}
      </PageContainer>
    </HeaderWrapper>
  );
}

export default Header;
